<template>
  <section class="section section-observed structure-page">
    <div class="structure-layout">
      <aside class="structure-sidebar">
        <button
          v-for="item in societyList"
          :key="item.associationId"
          type="button"
          class="sidebar-item"
          :class="{
            active: String(selectedAssociationId) === String(item.associationId),
            'is-main': isMainAssociation(item)
          }"
          @click="selectSociety(item.associationId)"
        >
          {{ displayAssociationName(item) }}
        </button>
      </aside>

      <div class="structure-main">
        <div class="section-heading structure-main-head">
          <div>
            <h2>{{ activeSocietyName }}</h2>
            <p class="muted-copy">左侧选择学会；右侧查看组织机构概况。可通过入口进入独立站点。</p>
          </div>
          <button
            v-if="selectedAssociationId != null"
            type="button"
            class="enter-site-btn"
            @click="enterSite"
          >
            进入独立站点
          </button>
        </div>

        <div v-if="loadingOverview" class="muted-copy">正在加载...</div>
        <article v-else-if="overviewPage && overviewPage.bodyContent" class="structure-article">
          <h3>{{ overviewPageTitle }}</h3>
          <div class="rich-body" v-html="overviewPage.bodyContent" />
        </article>
        <div v-else class="empty-panel">
          <p class="muted-copy">暂无「{{ activeSocietyName }}」组织机构概况，请在管理后台维护。</p>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { listPublicCmsEntries, unwrapPublicPage } from '../../api/paleo'
import {
  BRANCH_OVERVIEW_PAGE_CODE,
  SOCIETY_OVERVIEW_PAGE_CODE,
  BRANCH_ORGANIZATION_PAGE_CODE,
  branchSitePath,
  displayAssociationName,
  isMainAssociation,
  sortSocietiesMainFirst
} from '../../data/branchSite'

export default {
  name: 'StructurePage',
  inject: ['front'],
  data() {
    return {
      selectedAssociationId: null,
      overviewById: {},
      loadingOverview: false
    }
  },
  computed: {
    societyList() {
      return sortSocietiesMainFirst(this.front.bindableAssociations || [])
    },
    activeSociety() {
      return (
        this.societyList.find(
          item => String(item.associationId) === String(this.selectedAssociationId)
        ) || null
      )
    },
    activeSocietyName() {
      return this.activeSociety ? displayAssociationName(this.activeSociety) : '组织机构'
    },
    overviewPage() {
      if (this.selectedAssociationId == null) return null
      return this.overviewById[String(this.selectedAssociationId)] || null
    },
    overviewPageTitle() {
      // 组织机构页统一显示「组织机构」，不显示 CMS 条目自带的标题
      return (this.overviewPage && this.overviewPage.title) || '组织机构'
    }
  },
  watch: {
    societyList: {
      immediate: true,
      handler(list) {
        if (!list.length) return
        const q = this.$route.query.associationId
        const fromQuery = list.find(item => String(item.associationId) === String(q))
        const main = list.find(item => isMainAssociation(item)) || list[0]
        const nextId = fromQuery
          ? fromQuery.associationId
          : this.selectedAssociationId != null
            ? (list.find(item => String(item.associationId) === String(this.selectedAssociationId)) || main)
              .associationId
            : main.associationId
        if (String(this.selectedAssociationId) !== String(nextId)) {
          this.selectSociety(nextId, { replace: true })
        }
      }
    },
    '$route.query.associationId'(id) {
      if (id == null || id === '') return
      if (String(id) === String(this.selectedAssociationId)) return
      if (this.societyList.some(item => String(item.associationId) === String(id))) {
        this.selectSociety(id, { replace: true, skipRoute: true })
      }
    }
  },
  methods: {
    displayAssociationName,
    isMainAssociation,
    selectSociety(associationId, opts = {}) {
      this.selectedAssociationId = associationId
      if (!opts.skipRoute) {
        const q = String(this.$route.query.associationId || '')
        if (q !== String(associationId)) {
          this.$router
            .replace({
              path: '/structure',
              query: { associationId: String(associationId) }
            })
            .catch(() => {})
        }
      }
      this.ensureOverview(associationId)
    },
    async ensureOverview(associationId) {
      const key = String(associationId)
      if (Object.prototype.hasOwnProperty.call(this.overviewById, key)) return
      this.loadingOverview = true
      try {
        const result = await listPublicCmsEntries({
          moduleCode: 'pages',
          associationId,
          pageNum: 1,
          pageSize: 50
        })
        const rows = unwrapPublicPage(result).rows.map(item => this.front.mapCmsEntry(item))
        const society = this.societyList.find(item => String(item.associationId) === key)
        const isMain = society ? isMainAssociation(society) : false
        // 总会读「组织机构概况」(society_overview)，分会读「分会组织机构」(branch_organization)
        // 总会兼容回退：若尚无 society_overview 数据则读 branch_overview（旧数据）
        const preferredCode = isMain ? SOCIETY_OVERVIEW_PAGE_CODE : BRANCH_ORGANIZATION_PAGE_CODE
        const page = rows.find(item => item.columnCode === preferredCode)
          || (isMain ? rows.find(item => item.columnCode === BRANCH_OVERVIEW_PAGE_CODE) : null)
          || null
        this.$set(this.overviewById, key, page)
      } catch (e) {
        this.$set(this.overviewById, key, null)
      } finally {
        this.loadingOverview = false
      }
    },
    enterSite() {
      if (this.selectedAssociationId == null) return
      this.front.goPath(branchSitePath(this.selectedAssociationId, 'home'))
    }
  }
}
</script>

<style scoped>
.structure-layout {
  display: grid;
  grid-template-columns: 240px minmax(0, 1fr);
  gap: 24px;
  align-items: start;
}

.structure-sidebar {
  position: sticky;
  top: 96px;
  padding: 16px;
  border: 1px solid var(--border);
  border-radius: var(--front-radius);
  background: #fff;
  box-shadow: var(--shadow-soft);
}

.sidebar-item {
  display: block;
  width: 100%;
  margin-bottom: 6px;
  padding: 10px 12px;
  border: 1px solid transparent;
  border-radius: 8px;
  background: transparent;
  text-align: left;
  color: var(--text);
  cursor: pointer;
}

.sidebar-item:hover,
.sidebar-item.active {
  border-color: var(--border);
  background: rgba(0, 43, 73, 0.04);
  color: var(--primary);
  font-weight: 600;
}

.sidebar-item.is-main {
  font-weight: 700;
}

.structure-main-head {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;
}

.enter-site-btn {
  flex-shrink: 0;
  padding: 8px 16px;
  border: 1px solid var(--primary);
  border-radius: 8px;
  background: var(--primary);
  color: #fff;
  cursor: pointer;
}

.structure-article,
.empty-panel {
  padding: 24px;
  border: 1px solid var(--border);
  border-top: 3px solid var(--primary);
  border-radius: var(--front-radius);
  background: #fff;
  box-shadow: var(--shadow-soft);
}

.structure-article h3 {
  margin: 0 0 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--border);
  color: var(--primary);
}

.rich-body >>> img {
  max-width: 100%;
}

@media (max-width: 900px) {
  .structure-layout {
    grid-template-columns: 1fr;
  }

  .structure-sidebar {
    position: static;
  }
}
</style>
